import MarketingLayout from '../../components/marketing/MarketingLayout';
import { Card, Title, Text } from "@tremor/react";
import { useState } from 'react';
import BookingWidget from '../../components/BookingWidget';
import ServicePageNavigation from '../../components/ServicePageNavigation';
import { colors } from '../../lib/colors';

export default function EstatePreservation() {
  const [showBookingWidget, setShowBookingWidget] = useState(false);

  return (
    <MarketingLayout>
      <section className="py-16 px-4 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto relative">
          {/* Service Navigation */}
          <ServicePageNavigation currentSlug="estate-preservation" />
          {/* Header */}
          <div className="text-center mb-12">
            <Title className="text-4xl font-bold text-gray-800 dark:text-white mb-4">Estate Preservation Strategies</Title>
            <Text className="text-xl text-gray-600 dark:text-gray-400">
              Protecting Your Legacy: Ensuring Your Assets are Transferred Efficiently and Effectively
            </Text>
          </div>

          <div className="space-y-8">
            {/* What is It? */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">What is Estate Preservation?</Title>
              <Text className="text-gray-600 dark:text-gray-300">
                Estate preservation involves implementing strategies to minimize taxes, administrative costs, and potential conflicts upon your death, ensuring that the maximum value of your estate is transferred to your intended beneficiaries according to your wishes.
              </Text>
              <Text className="text-gray-600 dark:text-gray-300 mt-2">
                A key objective is to provide liquidity to cover taxes (like capital gains on deemed disposition), probate fees, debts, and other settlement costs without forcing the sale of cherished assets, businesses, or properties, often under unfavorable conditions. Life insurance is a cornerstone tool in achieving this goal.
              </Text>
            </Card>

            {/* Key Challenges Addressed */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Common Estate Challenges Addressed</Title>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1 mt-4">
                <li>
                  <strong>Taxes on Death:</strong> Canada has a deemed disposition rule, meaning assets like non-registered investments, cottages, and businesses may trigger significant capital gains taxes upon death, creating a large liability for the estate.
                </li>
                <li>
                  <strong>Probate Fees:</strong> Provincial fees levied on the value of assets passing through the Will can reduce the net inheritance.
                </li>
                <li>
                  <strong>Estate Illiquidity:</strong> Lack of readily available cash to pay taxes and expenses can force the sale of illiquid assets (e.g., family farm, business shares, real estate) at inopportune times.
                </li>
                <li>
                  <strong>Business Succession:</strong> Ensuring a smooth transition of a family business without crippling the operations due to tax burdens or lack of funding for buy-sell agreements.
                </li>
                <li>
                  <strong>Estate Equalization:</strong> Fairly distributing assets among beneficiaries, especially when some assets are indivisible or have varying values (e.g., a farm to one child, investments to another).
                </li>
                 <li>
                  <strong>Debt Repayment:</strong> Covering outstanding mortgages, loans, or lines of credit.
                </li>
              </ul>
            </Card>

            {/* Strategies (How It Works) */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Key Estate Preservation Strategies</Title>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-2">
                 <li>
                    <strong>Life Insurance:</strong> Providing a tax-free death benefit directly to the estate or named beneficiaries to instantly create the liquidity needed for taxes, debts, and equalization payments. Policies can be personally owned, corporately owned, or held within trusts.
                 </li>
                 <li>
                    <strong>Will & Trust Planning:</strong> Properly structured Wills and the potential use of trusts (e.g., testamentary trusts, alter ego trusts, joint partner trusts) can help minimize probate fees, manage assets for beneficiaries, and provide creditor protection.
                 </li>
                 <li>
                    <strong>Ownership Structuring:</strong> Holding assets jointly (JTWROS) can bypass probate (but may have other implications). Utilizing corporate structures can facilitate business succession and potentially cap tax liabilities (e.g., through estate freezes).
                 </li>
                 <li>
                    <strong>Planned Giving:</strong> Incorporating charitable donations into the estate plan can generate tax credits to offset estate taxes.
                 </li>
                  <li>
                    <strong>Asset Gifting:</strong> Gifting assets during one's lifetime can reduce the value of the final estate subject to tax and probate, though this needs careful planning regarding attribution rules and loss of control.
                 </li>
              </ul>
            </Card>

            {/* Key Benefits */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Benefits of Proactive Estate Preservation</Title>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Maximize Inheritance:</strong> Reduces the impact of taxes and fees, leaving more for your beneficiaries.</li>
                <li><strong>Asset Protection:</strong> Avoids forced liquidation of key assets to cover estate costs.</li>
                <li><strong>Liquidity:</strong> Ensures sufficient cash is available precisely when needed.</li>
                <li><strong>Certainty & Control:</strong> Provides clarity on how assets will be distributed and managed.</li>
                <li><strong>Business Continuity:</strong> Facilitates smooth succession planning for business owners.</li>
                <li><strong>Peace of Mind:</strong> Knowing your affairs are in order and your legacy is protected.</li>
              </ul>
            </Card>

             {/* Suitability */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Who Needs Estate Preservation Planning?</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">While everyone with assets can benefit from basic planning (like having a Will), comprehensive estate preservation strategies are particularly crucial for:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li>Individuals with significant assets in non-registered accounts, real estate (especially secondary properties), or private company shares.</li>
                <li>Business owners concerned about succession and funding buy-sell agreements.</li>
                <li>Individuals with complex family structures or specific distribution wishes.</li>
                <li>Those wanting to minimize taxes and maximize the legacy passed to heirs or charities.</li>
                <li>Anyone anticipating a substantial tax liability upon death.</li>
              </ul>
            </Card>

            {/* Call to Action */}
            <div className="text-center mt-12">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-4">Secure Your Legacy for Generations</Title>
              <Text className="text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
                Don't let taxes and unforeseen costs erode the value of your hard-earned estate. Let's develop a personalized strategy to preserve your wealth and ensure your wishes are fulfilled.
              </Text>
              <button
                onClick={() => setShowBookingWidget(true)}
                className="px-8 py-3 rounded-md text-lg font-semibold text-white transition-transform duration-150 ease-in-out hover:scale-105 active:scale-95"
                style={{ background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)` }}
              >
                Plan Your Estate Preservation
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Widget Modal */}
      {showBookingWidget && (
         <BookingWidget onClose={() => setShowBookingWidget(false)} />
      )}
    </MarketingLayout>
  );
}